import { LitElement, PropertyValues, html, nothing } from 'lit';
import { applyNhsGlobal } from '@ui/styles/applyNhsGlobal';
import sessionWarningModalStyles from '@ui/styles/nhs-subsets/nhs-session-warning-modal.scss?inline';

export interface SessionWarningModalViewModel {
  open: boolean;
  title: string;
  message: string;
  secondsRemaining: number;
}

export class SessionWarningModal extends LitElement {
  public static readonly properties = {
    onLogOut: { attribute: false },
    onStaySignedIn: { attribute: false },
    viewModel: { attribute: false }
  };

  public onLogOut?: () => void;

  public onStaySignedIn?: () => void;

  public viewModel: SessionWarningModalViewModel = {
    open: false,
    title: '',
    message: '',
    secondsRemaining: 0
  };

  private previouslyFocusedElement: HTMLElement | null = null;

  private restoreFocusOnClose = false;

  protected createRenderRoot(): ShadowRoot | HTMLElement {
    return applyNhsGlobal(this, sessionWarningModalStyles);
  }

  public disconnectedCallback(): void {
    super.disconnectedCallback();
    this.restorePreviousFocus();
  }

  protected willUpdate(changedProperties: PropertyValues<this>): void {
    if (this.viewModel.open && !wasOpen(changedProperties)) {
      this.previouslyFocusedElement = findDeepActiveElement();
    }
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    const previouslyOpen = wasOpen(changedProperties);
    if (!this.viewModel.open) {
      if (previouslyOpen) {
        this.restorePreviousFocus();
      }

      return;
    }

    if (!previouslyOpen) {
      this.renderRoot.querySelector<HTMLElement>('.timeout-modal')?.focus();
    }
  }

  public render() {
    if (!this.viewModel.open) {
      return nothing;
    }

    return html`
      <div class="timeout-overlay" aria-hidden="true"></div>
      <section
        class="timeout-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-warning-title"
        aria-describedby="session-warning-message"
        tabindex="-1"
        @keydown=${this.handleModalKeydown}
      >
        <h2 class="timeout-modal__heading" id="session-warning-title">${this.viewModel.title}</h2>
        <div class="timeout-modal__body">
          <p id="session-warning-message">
            ${this.viewModel.message}
            <strong><span class="countdown">${this.viewModel.secondsRemaining}</span> ${this.viewModel.secondsRemaining === 1 ? 'second' : 'seconds'}</strong>.
          </p>
        </div>
        <div class="timeout-modal__buttons">
          <button class="nhsuk-button" type="button" @click=${this.handleStaySignedIn}>Stay signed in</button>
          <button class="timeout-modal__link" type="button" @click=${this.handleLogOut}>Log out</button>
        </div>
      </section>
    `;
  }

  private restorePreviousFocus(): void {
    const target = this.restoreFocusOnClose ? this.previouslyFocusedElement : null;
    this.restoreFocusOnClose = false;
    this.previouslyFocusedElement = null;

    if (target?.isConnected) {
      target.focus();
    }
  }

  private readonly handleLogOut = (): void => {
    this.restoreFocusOnClose = false;
    this.onLogOut?.();
  };

  private readonly handleStaySignedIn = (): void => {
    this.restoreFocusOnClose = true;
    this.onStaySignedIn?.();
  };

  private readonly handleModalKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') {
      return;
    }

    const modal = this.renderRoot.querySelector<HTMLElement>('.timeout-modal');
    const focusableElements = Array.from(
      modal?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ?? []
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = this.shadowRoot?.activeElement;

    if (event.shiftKey && (activeElement === firstElement || activeElement === modal)) {
      event.preventDefault();
      lastElement?.focus();
      return;
    }

    if (!event.shiftKey && (activeElement === lastElement || activeElement === modal)) {
      event.preventDefault();
      firstElement?.focus();
    }
  };
}

customElements.define('inav-session-warning-modal', SessionWarningModal);

function wasOpen(changedProperties: PropertyValues<SessionWarningModal>): boolean {
  const previousViewModel = changedProperties.get('viewModel') as SessionWarningModalViewModel | undefined;
  return previousViewModel?.open === true;
}

function findDeepActiveElement(): HTMLElement | null {
  let active: Element | null = document.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }

  return active instanceof HTMLElement && active !== document.body ? active : null;
}

declare global {
  interface HTMLElementTagNameMap {
    'inav-session-warning-modal': SessionWarningModal;
  }
}